import { useEffect, useMemo, useState } from "react";

/* Presentation-only grouping over an existing flat nav config (which stays the
   functional SSOT for routes/permissions/feature gates). Items whose `to` is
   not listed in any group render as top-level. Groups with no visible items
   are dropped. The group containing the active route is always revealed. */
export function groupNavItems(items, groups) {
  const byTo = new Map(items.map((i) => [i.to, i]));
  const grouped = new Set();
  const out = [];
  groups.forEach((g) => {
    const children = g.items.map((to) => byTo.get(to)).filter(Boolean);
    children.forEach((c) => grouped.add(c.to));
    if (children.length) out.push({ type: "group", key: g.key, label: g.label, icon: g.icon, items: children, position: g.position || "after" });
  });
  const top = items.filter((i) => !grouped.has(i.to)).map((i) => ({ type: "item", key: i.to, item: i }));
  // Top-level items keep their original order and appear before the groups.
  return [...top, ...out];
}

export function useNavGroups(items, groups, isActive) {
  const entries = useMemo(() => groupNavItems(items, groups), [items, groups]);
  const activeGroupKey = useMemo(
    () => entries.find((e) => e.type === "group" && e.items.some(isActive))?.key || null,
    [entries, isActive],
  );
  const [open, setOpen] = useState({});
  useEffect(() => {
    if (activeGroupKey) setOpen((s) => (s[activeGroupKey] ? s : { ...s, [activeGroupKey]: true }));
  }, [activeGroupKey]);
  const isOpen = (key) => open[key] === undefined ? key === activeGroupKey : open[key];
  const toggle = (key) => setOpen((s) => ({ ...s, [key]: !isOpen(key) }));
  return { entries, isOpen, toggle, activeGroupKey };
}
